/**
 * Options and helpers for filtering homeowners by "Workscope Goodwill" and "Status of Workscope Goodwill".
 * Slugs match backend priority goodwill keys (e.g. tree-work-goodwill, muck-and-gut-interior-goodwill).
 */

export type WorkscopeGoodwillOption = { value: string; label: string }

/** Slug -> { onPath, statusPath } relative to home object (no "home." prefix). */
const SLUG_TO_PATHS: Record<string, { onPath: string; statusPath: string }> = {
  'tree-work-goodwill': { onPath: 'treeDebrisInfo.goodwillTreeWorkOn', statusPath: 'treeDebrisInfo.goodwillTreeWorkNeeded' },
  'exterior-debris-removal-goodwill': { onPath: 'treeDebrisInfo.goodwillExteriorDebrisOn', statusPath: 'treeDebrisInfo.goodwillExteriorDebrisRemovalNeeded' },
  'heavy-equipment-demo-goodwill': { onPath: 'treeDebrisInfo.goodwillHeavyEquipmentOn', statusPath: 'treeDebrisInfo.goodwillHeavyEquipmentNeeded' },
  'install-roofing-goodwill': { onPath: 'roofingInfo.goodwillRoofingOn', statusPath: 'roofingInfo.goodwillRoofing' },
  'tarp-roof-goodwill': { onPath: 'roofingInfo.goodwillRoofTarpOn', statusPath: 'roofingInfo.goodwillRoofTarpNeeded' },
  'insulate-crawl-space-goodwill': { onPath: 'crawlSpaceInfo.goodwillCrawlSpaceInsulationOn', statusPath: 'crawlSpaceInfo.goodwillCrawlSpaceInsulation' },
  'm-and-g-crawl-space-goodwill': { onPath: 'crawlSpaceInfo.goodwillCrawlSpaceMuckOn', statusPath: 'crawlSpaceInfo.goodwillCrawlSpaceMuck' },
  'muck-and-gut-interior-goodwill': { onPath: 'muckGutInfo.goodwillMuckGutNeededOn', statusPath: 'muckGutInfo.goodwillMuckGutNeeded' },
  'mold-remediation-goodwill': { onPath: 'moldRemediationInfo.goodwillMoldRemediationOn', statusPath: 'moldRemediationInfo.goodwillMoldRemediation' },
  'rebuild-prep-goodwill': { onPath: 'rebuildPrepInfo.goodwillRebuildPrepOn', statusPath: 'rebuildPrepInfo.goodwillRebuildPrep' },
  'rough-carpentry-goodwill': { onPath: 'roughCarpentryInfo.goodwillRoughCarpentryOn', statusPath: 'roughCarpentryInfo.goodwillRoughCarpentry' },
  'electrical-fixtures-goodwill': { onPath: 'electricalInfo.goodwillElectricalOn', statusPath: 'electricalInfo.goodwillElectrical' },
  'rough-electrical-goodwill': { onPath: 'electricalInfo.goodwillElectricalRoughInOn', statusPath: 'electricalInfo.goodwillElectricalRoughIn' },
  'hvac-final-goodwill': { onPath: 'hvacInfo.goodwillHvacOn', statusPath: 'hvacInfo.goodwillHvac' },
  'rough-mechanical-goodwill': { onPath: 'hvacInfo.goodwillMechanicalRoughInOn', statusPath: 'hvacInfo.goodwillMechanicalRoughIn' },
  'plumbing-fixtures-goodwill': { onPath: 'plumbingInfo.goodwillPlumbingOn', statusPath: 'plumbingInfo.goodwillPlumbing' },
  'rough-plumbing-goodwill': { onPath: 'plumbingInfo.goodwillPlumbingRoughInOn', statusPath: 'plumbingInfo.goodwillPlumbingRoughIn' },
  'insulation-goodwill': { onPath: 'insulationInstallInfo.goodwillInsulationInstallOn', statusPath: 'insulationInstallInfo.goodwillInsulationInstall' },
  'drywall-hanging-goodwill': { onPath: 'drywallHangingInfo.goodwillDrywallHangingOn', statusPath: 'drywallHangingInfo.goodwillDrywallHanging' },
  'mud-and-tape-goodwill': { onPath: 'mudAndTapeInfo.goodwillMudAndTapeOn', statusPath: 'mudAndTapeInfo.goodwillMudAndTape' },
  'block-wall-goodwill': { onPath: 'blockWallInfo.goodwillBlockWallRepairOn', statusPath: 'blockWallInfo.goodwillBlockWallRepair' },
  'stucco-goodwill': { onPath: 'stuccoInfo.goodwillStuccoOn', statusPath: 'stuccoInfo.goodwillStucco' },
  'bathroom-rebuild-goodwill': { onPath: 'bathroomRebuildInfo.goodwillBathroomRebuildOn', statusPath: 'bathroomRebuildInfo.goodwillBathroomRebuild' },
  'install-cabinets-goodwill': { onPath: 'cabinetInstallInfo.goodwillCabinetInstallOn', statusPath: 'cabinetInstallInfo.goodwillCabinetInstall' },
  'install-counters-goodwill': { onPath: 'cabinetInstallInfo.goodwillCounterInstallOn', statusPath: 'cabinetInstallInfo.goodwillCounterInstall' },
  'install-appliances-goodwill': { onPath: 'cabinetInstallInfo.goodwillApplianceInstallOn', statusPath: 'cabinetInstallInfo.goodwillApplianceInstall' },
  'interior-paint-goodwill': { onPath: 'interiorPaintInfo.goodwillInteriorPaintOn', statusPath: 'interiorPaintInfo.goodwillInteriorPaint' },
  'flooring-install-goodwill': { onPath: 'flooringInstallInfo.goodwillFlooringInstallOn', statusPath: 'flooringInstallInfo.goodwillFlooringInstall' },
  'install-interior-doors-goodwill': { onPath: 'installInteriorDoorsInfo.goodwillInstallInteriorDoorsOn', statusPath: 'installInteriorDoorsInfo.goodwillInstallInteriorDoors' },
  'trim-install-goodwill': { onPath: 'trimInstallInfo.goodwillTrimInstallOn', statusPath: 'trimInstallInfo.goodwillTrimInstall' },
  'install-exterior-doors-goodwill': { onPath: 'exteriorDoorInstallInfo.goodwillExteriorDoorInstallOn', statusPath: 'exteriorDoorInstallInfo.goodwillExteriorDoorInstall' },
  'repair-driveway-goodwill': { onPath: 'drivewayRepair.goodwillDrivewayRepairOn', statusPath: 'drivewayRepair.goodwillDrivewayRepair' },
  'shutter-install-goodwill': { onPath: 'stormShutters.goodwillShutterInstallOn', statusPath: 'stormShutters.goodwillShutterInstall' },
  'miscellaneous-goodwill': { onPath: 'miscellaneousInfo.miscellaneous', statusPath: 'miscellaneousInfo.miscellaneous' }, // no separate on; status field indicates use
}

function getNested(obj: unknown, path: string): unknown {
  return path.split('.').reduce((cur: unknown, key) => (cur as Record<string, unknown>)?.[key], obj)
}

/**
 * Returns the status string for the given goodwill slug if that section is "on", else null.
 * For "miscellaneous-goodwill", the status field itself indicates use (treat as on if has a status value).
 */
export function getGoodwillStatusForSlug(home: Record<string, unknown>, slug: string): string | null {
  const paths = SLUG_TO_PATHS[slug]
  if (!paths) return null
  const onVal = getNested(home, paths.onPath)
  const statusVal = getNested(home, paths.statusPath)
  // Miscellaneous: no on toggle; if status is set (and not empty/No Need), consider it "on"
  if (slug === 'miscellaneous-goodwill') {
    const s = statusVal != null && String(statusVal).trim() !== '' ? String(statusVal).trim() : null
    return s || null
  }
  if (!onVal) return null
  const on = typeof onVal === 'boolean' ? onVal : String(onVal).toLowerCase() === 'true'
  if (!on) return null
  const status = statusVal != null && String(statusVal).trim() !== '' ? String(statusVal).trim() : null
  return status
}

/**
 * Options for the "Workscope Goodwill" multiselect filter (including Miscellaneous Goodwill).
 */
export const WORK_SCOPE_GOODWILL_OPTIONS: WorkscopeGoodwillOption[] = [
  { value: 'tree-work-goodwill', label: 'Tree Work Goodwill' },
  { value: 'exterior-debris-removal-goodwill', label: 'Exterior Debris Goodwill' },
  { value: 'heavy-equipment-demo-goodwill', label: 'Heavy Equipment/Demo Goodwill' },
  { value: 'install-roofing-goodwill', label: 'Roofing Goodwill' },
  { value: 'tarp-roof-goodwill', label: 'Roof Tarp Goodwill' },
  { value: 'insulate-crawl-space-goodwill', label: 'Crawl Space Insulation Goodwill' },
  { value: 'm-and-g-crawl-space-goodwill', label: 'M&C Crawl Space Goodwill' },
  { value: 'muck-and-gut-interior-goodwill', label: 'Muck & Gut Goodwill' },
  { value: 'mold-remediation-goodwill', label: 'Mold Remediation Goodwill' },
  { value: 'rebuild-prep-goodwill', label: 'Rebuild Prep Goodwill' },
  { value: 'rough-carpentry-goodwill', label: 'Rough Carpentry Goodwill' },
  { value: 'electrical-fixtures-goodwill', label: 'Electrical Goodwill' },
  { value: 'rough-electrical-goodwill', label: 'Rough Electrical Goodwill' },
  { value: 'hvac-final-goodwill', label: 'HVAC Goodwill' },
  { value: 'rough-mechanical-goodwill', label: 'Rough Mechanical Goodwill' },
  { value: 'plumbing-fixtures-goodwill', label: 'Plumbing Goodwill' },
  { value: 'rough-plumbing-goodwill', label: 'Rough Plumbing Goodwill' },
  { value: 'insulation-goodwill', label: 'Insulation Goodwill' },
  { value: 'drywall-hanging-goodwill', label: 'Drywall Hanging Goodwill' },
  { value: 'mud-and-tape-goodwill', label: 'Mud and Tape Goodwill' },
  { value: 'block-wall-goodwill', label: 'Block Wall Goodwill' },
  { value: 'stucco-goodwill', label: 'Stucco Goodwill' },
  { value: 'bathroom-rebuild-goodwill', label: 'Bathroom Rebuild Goodwill' },
  { value: 'install-cabinets-goodwill', label: 'Cabinet Install Goodwill' },
  { value: 'install-counters-goodwill', label: 'Counter Install Goodwill' },
  { value: 'install-appliances-goodwill', label: 'Appliance Install Goodwill' },
  { value: 'interior-paint-goodwill', label: 'Interior Paint Goodwill' },
  { value: 'flooring-install-goodwill', label: 'Flooring Install Goodwill' },
  { value: 'install-interior-doors-goodwill', label: 'Install Interior Doors Goodwill' },
  { value: 'trim-install-goodwill', label: 'Trim Install Goodwill' },
  { value: 'install-exterior-doors-goodwill', label: 'Exterior Door Install Goodwill' },
  { value: 'repair-driveway-goodwill', label: 'Driveway Repair Goodwill' },
  { value: 'shutter-install-goodwill', label: 'Storm Shutters Goodwill' },
  { value: 'miscellaneous-goodwill', label: 'Miscellaneous Goodwill' },
]

/** Map display-style or API workscope values to canonical slug (matches backend alias logic). */
const WORKSCOPE_TO_CANONICAL: Record<string, string> = {
  roofing: 'install-roofing',
  'roof-tarp': 'tarp-roof',
  misc: 'miscellaneous',
}

function toCanonicalWorkscopeSlug(value: string): string {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')
  return WORKSCOPE_TO_CANONICAL[normalized] ?? normalized
}

/**
 * Returns only the Goodwill Workscope options that correspond to workscopes active in the region.
 * Pass the region's work scope values (e.g. from useWorkScopes().workScopeOptions .value).
 * When regionWorkScopeValues is empty (e.g. no region or loading), returns full list for backwards compatibility.
 */
export function getGoodwillOptionsForRegion(
  regionWorkScopeValues: string[]
): WorkscopeGoodwillOption[] {
  if (!Array.isArray(regionWorkScopeValues) || !regionWorkScopeValues.length) return WORK_SCOPE_GOODWILL_OPTIONS
  const canonicalSlugs = new Set(
    regionWorkScopeValues.map((v) => toCanonicalWorkscopeSlug(v))
  )
  return WORK_SCOPE_GOODWILL_OPTIONS.filter((opt) => {
    const baseSlug = opt.value.replace(/-goodwill$/, '')
    return canonicalSlugs.has(baseSlug)
  })
}
